import { RouteDestination } from "@/components/Passenger/models/RouteWithTrips";
import moment from "moment";
import { Feature } from "ol";
import { Point } from "ol/geom";
import { fromLonLat } from "ol/proj";
import { Style, Icon, Text, Fill, Stroke } from "ol/style";
import { defaultStyles } from "@/components/Map/DefaultStyles";

interface ICachedDestinationStyles {
  default: Style | null;
  selected: Style[] | null;
}

export default class DestinationFeature extends Feature {
  destination: RouteDestination;
  isSelected = false;
  private readonly cachedStyle: ICachedDestinationStyles = { default: null, selected: null };
  private readonly timeLabel: string;
  private readonly textLabel: string;

  constructor(destination: RouteDestination, passengerName?: string) {
    if (isNaN(destination.longitude) || isNaN(destination.latitude)) {
      throw new Error('Invalid coordinates for DestinationFeature.');
    }
    super({ geometry: new Point(fromLonLat([destination.longitude, destination.latitude])) });

    this.destination = destination;
    this.timeLabel = this.destination.time ? moment().startOf('day').add(this.destination.time, 'minutes').format('HH:mm') : '?';
    this.textLabel = this.getTextLabel(passengerName);
    this.cachedStyle.default = this.getDefaultStyle();
    this.cachedStyle.selected = this.getSelectedStyle();
    this.setStyle(this.cachedStyle.default);
  }

  private getTextLabel(passengerName?: string) {
    if (this.destination.trips.length > 1) {
      return `(${this.destination.trips.length}) ${this.destination.address}`;
    }
    return passengerName || this.destination.address;
  }

  /** Get the default pin style for the destination. */
  private getDefaultStyle(color: string = defaultStyles.colors.default) {
    const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" version="1.1" width="${defaultStyles.icon.size}" height="${defaultStyles.icon.size}">
      <path stroke-width="${defaultStyles.strokeWidth}" stroke="${defaultStyles.colors.dark}" fill="${color}" transform="rotate(180 32,32)" d="m24.245133,62.173094c-9.350148,-3.276338 -16.052023,-13.686837 -14.528956,-22.568879c0.9604,-5.600801 21.222429,-39.60157 22.991945,-38.581738c0.577547,0.332858 5.71699,8.259864 11.420986,17.615572c9.137201,14.986857 10.370895,17.781443 10.370895,23.492337c0,11.575748 -9.934582,21.057598 -21.863107,20.866783c-3.480912,-0.05569 -7.257207,-0.426523 -8.391763,-0.824074l0,-0.000002z"/>
    </svg>`;

    return new Style({
      image: new Icon({
        src: 'data:image/svg+xml;utf8,' + encodeURIComponent(svg),
        anchor: [0.5, 1],
        scale: defaultStyles.icon.scale,
      }),
    });
  }

  /** Get the style for the selected destination. Includes pin style, address/passenger and time label. */
  private getSelectedStyle() {
    const pin = this.getDefaultStyle(defaultStyles.colors.selected);
    const offset = -defaultStyles.icon.size * defaultStyles.icon.scale;
    const labelText = this.textLabel;

    const label = new Style({
      text: new Text({
        offsetY: offset - 26,
        text: labelText,
        font: defaultStyles.font,
        fill: new Fill({ color: defaultStyles.colors.light }),
        stroke: new Stroke({ color: defaultStyles.colors.dark, width: defaultStyles.strokeWidth }),
      }),
    });

    const time = new Style({
      text: new Text({
        text: this.timeLabel,
        offsetY: offset - 8,
        font: defaultStyles.font,
        fill: new Fill({ color: defaultStyles.colors.light }),
        stroke: new Stroke({ color: defaultStyles.colors.dark, width: defaultStyles.strokeWidth }),
      }),
    });

    return [pin, label, time];
  }

  toggleSelection() {
    this.isSelected = !this.isSelected;
    const style = this.isSelected ? this.cachedStyle.selected : this.cachedStyle.default;
    this.setStyle(style);
  }
}
